import { useQuery } from "react-query"
import { fetchSingleLoad, cleanLoads, eventApi } from "./apis"
import { groupProductsByTerminals } from "./mappers"
import axios from "axios"
import { DELIVERY } from "types/"
import { timeout, convertLoad } from "utils"
import { useEffect } from "react"
import _groupBy from "lodash/groupBy"

export const useAddPickupProducts = (load_id: string) => {
  const { refetch } = useQuery(["load", load_id], fetchSingleLoad(load_id), {
    enabled: false,
  })

  const removeProductFromPickup = async (product: any) => {
    const load = await axios.post("/event/loads/", {
      type: "DELETE_PICKUP_PRODUCT",
      load_id,
      data: {
        pickup_product_id: product.id,
      },
    })
    await eventApi("MATCH_PICKUP_DELIVERY", load_id)
    return convertLoad(load.data)
  }

  const productApiMapper = (product: any) => ({
    product_id: product?.product?.id || product?.product,
    expected_gross_quantity: Number(product?.gross_quantity),
    preferences: product?.preferences,
  })

  const tempMerge = (products: any) => {
    const newProducts = _groupBy(
      products,
      (product: any) =>
        `${product.product}:${product.terminal}:${product.supplier}:${product.loading_number_id}`
    )

    return Object.values(newProducts).map((products) =>
      products.reduce((acc, product) => {
        if (!acc) return product
        acc.gross_quantity += product.gross_quantity
        return acc
      }, null)
    )
  }

  const mergeProducts = (pickupProducts: any, newProducts: any) => {
    const newPickupProducts =
      pickupProducts.map((product: any) => ({
        id: product?.id,
        pickup_product_id: product?.id,
        product_id: product?.product_id || product?.product,
        expected_gross_quantity: Number(
          product?.expected_gross_quantity || product?.gross_quantity
        ),
        preferences: product?.preferences?.map((pref: any) => ({
          supplier_id: pref?.supplier?.id,
          terminal_id: pref?.terminal?.id,
          loading_number: pref?.loading_number,
          loading_number_id: pref?.loading_number?.id,
        })),
      })) ?? []

    newProducts.forEach((product: any) => {
      // If product already exists in delivery, add the quantity
      const existingIndex = newPickupProducts.findIndex((ed: any) => {
        const edPref = ed?.preferences?.[0] ?? {}
        const productPref = product?.preferences?.[0] ?? {}
        return (
          ed?.product_id === (product?.product_id || product?.product) &&
          (edPref?.supplier_id || null) ===
            (productPref?.supplier_id || null) &&
          (edPref?.terminal_id || null) ===
            (productPref?.terminal_id || null) &&
          (edPref?.loading_number || null) ===
            (productPref?.loading_number || null)
        )
      })

      // if(true)
      newPickupProducts.push({
        product_id: product?.product_id || product?.product,
        expected_gross_quantity: Number(
          product?.gross_quantity || product?.expected_gross_quantity
        ),
        preferences: product.preferences,
      })
      return

      // If id is present, override the quantity, else add the quantity
      // const oldQuantity = product.id
      //   ? 0
      //   : Number(newPickupProducts[existingIndex].expected_gross_quantity)

      // newPickupProducts[existingIndex].expected_gross_quantity =
      //   oldQuantity +
      //   Number(product?.expected_gross_quantity || product?.gross_quantity)
      // newPickupProducts[existingIndex].preferences = product.preferences
    })

    return newPickupProducts
  }

  const addProducts = async (products?: any, pickup_id?: string) => {
    console.time("Add Pick: refetch load api")
    let { data: load } = await refetch()
    console.timeEnd("Add Pick: refetch load api")

    let retLoad = {} as any

    // products = tempMerge(products)

    const terminalsProducts = groupProductsByTerminals(products)

    // When editing a pickup, adjust and organize products in existing pickups with same terminal
    // If there is no pickup with this terminal, create it
    if (pickup_id) {
      const terminal = Object.keys(terminalsProducts)?.[0]
      const terminalProducts = terminalsProducts?.[terminal]

      let pickupsProductsRemaining = load.pickups
        ?.filter((p: any) => !p?.completed && p.id === pickup_id)?.[0]
        ?.products.filter((p: any) => {
          const product = terminalProducts?.find(
            (tp: any) => tp?.product === p?.product?.id
          )
          return !product
        })

      const newPickupProducts = terminalProducts.map(productApiMapper)

      const data = {
        products: newPickupProducts,
        pickup_id,
      } as any

      if (!terminal) {
        pickupsProductsRemaining = [
          ...pickupsProductsRemaining,
          ...newPickupProducts,
        ]
      }

      if (pickupsProductsRemaining?.length) {
        pickupsProductsRemaining = pickupsProductsRemaining.map(
          (product: any) => ({
            product_id: product?.product?.id,
            expected_gross_quantity: Number(
              product.expected_gross_quantity || product.gross_quantity
            ),
            preferences: product.preferences?.map((pref: any) => ({
              supplier_id: pref.supplier?.id,
              terminal_id: pref.terminal?.id,
              loading_number: pref.loading_number,
              loading_number_id: pref.loading_number?.id,
            })),
          })
        )
        // Update pickup removing products from this pickup that has a new terminal
        // Will find a new pickup for the products that changed terminal
        await eventApi("UPDATE_PICKUP", load_id, {
          products: pickupsProductsRemaining,
          pickup_id,
        })
      }

      // Sync other pickups
      const existingPickupTerminal = load.pickups?.filter(
        (p: any) =>
          !p?.completed &&
          parseInt(p?.terminal?.id) ===
            parseInt(Object.keys(terminalsProducts)?.[0])
      )

      // Not very sure why this was added. Doesnt seem to be adding value
      // if (
      //   !pickupsProductsRemaining?.length &&
      //   !existingPickupTerminal?.length
      // ) {
      //   retLoad = await eventApi("UPDATE_PICKUP", load_id, data)
      //   return retLoad
      // }

      if (!existingPickupTerminal?.length) {
        // If there is no pickup with this terminal, create it
        retLoad = await eventApi("CREATE_PICKUP", load_id, data)
        return retLoad
      }
      // Format existing pickup products for the merge
      const existingProducts =
        existingPickupTerminal?.[0]?.products.map((p: any) => ({
          ...p,
          product: p?.product?.id,
        })) ?? []
      const mergedProducts = mergeProducts(existingProducts, newPickupProducts)
      data.products = mergedProducts
      data.pickup_id = existingPickupTerminal?.[0]?.id
      retLoad = await eventApi("UPDATE_PICKUP", load_id, data)

      if (!pickupsProductsRemaining?.length) {
        // Delete Pickup as will be empty after update products and moving it to another pickup
        await axios.post("event/loads/", {
          type: "DELETE_PICKUP",
          load_id,
          data: {
            pickup_id,
          },
        })
        return retLoad
      }
      return retLoad
    }

    for (const terminal in terminalsProducts) {
      // if old_terminal !== terminal remove pickup from old terminal
      // get pending pickups in this terminal

      const terminalProducts = terminalsProducts[terminal]

      let newPickupProducts = terminalProducts.map(productApiMapper)

      // Check if product terminal has changed and remove it
      for (const product of terminalProducts) {
        if (
          product.id &&
          product.id.startsWith("p") &&
          product.oldTerminal !== terminal
        ) {
          console.time("Add Pick: remove products")
          load = await removeProductFromPickup(product)
          console.timeEnd("Add Pick: remove products")
          await timeout(100)
        }
      }

      const pending_pickup = load.pickups?.filter((p: any) =>
        isNaN(parseFloat(terminal))
          ? p.terminal === null
          : parseFloat(p.terminal?.id) === parseFloat(terminal) && !p.completed
      )[0]

      if (pending_pickup) {
        // Merge products
        newPickupProducts = mergeProducts(
          pending_pickup.products.map((product: any) => ({
            ...product,
            product: product?.product?.id,
          })),
          newPickupProducts
        )
      }
      const data = {
        products: newPickupProducts,
      } as any

      if (pending_pickup) {
        console.time("Add Pick: update api")
        data.pickup_id = pending_pickup.id

        retLoad = await eventApi("UPDATE_PICKUP", load_id, data)
        console.timeEnd("Add Pick: update api")
      } else {
        console.time("Add Pick: create api")
        const load = await eventApi("CREATE_PICKUP", load_id, data)
        retLoad = load
        console.timeEnd("Add Pick: create api")
      }
      await eventApi("MATCH_PICKUP_DELIVERY", load_id, {})
    }

    return retLoad
  }

  return addProducts
}

export const useAddDelivery = (load_id: string | undefined) => {
  const addPickupProducts = useAddPickupProducts(load_id ?? "")
  const { refetch } = useQuery(["load", load_id], fetchSingleLoad(load_id), {
    enabled: false,
  })

  const addDelivery = async (
    data?: any,
    delivery_id?: string,
    createEmptyPickups?: boolean,
    load?: any
  ) => {
    if (!data?.delivery_products?.length) {
      alert("No products to deliver")
      return false
    }
    if (!data.customer) {
      alert("No customer to deliver to")
      return false
    }

    const deliveryProducts = (data?.delivery_products ?? []).filter(
      (p: any) => createEmptyPickups || !!p.preferred_source || !!p.components
    )

    const shouldSkipCreate = [3, 4, 7].includes(load.status_code)

    // Dont add pickup product if status is  3,4,7 DAS-5221
    if (!shouldSkipCreate) {
      console.time("Add Del: add pickup")
      load = await addPickupProducts(deliveryProducts)
      console.timeEnd("Add Del: add pickup")
    }

    // console.time('Add Del: refetch load api')
    // // Get all deliveries in the load
    // const { data: load } = await refetch()
    // console.timeEnd('Add Del: refetch load api')
    // Find deliveries to this customer

    const existingDelivery = load.deliveries?.filter(
      (delivery: DELIVERY) =>
        (delivery.customer.id === data.customer &&
          delivery.location.id === data.location) ||
        delivery.id == delivery_id
    )?.[0]
    let newDeliveryProducts = data.delivery_products.map((product: any) => ({
      ...(product.price ? { price: product.price } : {}),
      cost_basis: product?.cost_basis
        ? parseFloat(product?.cost_basis)?.toFixed(4)
        : null,
      product_id: product.product,
      tanks:
        product.tank_id === undefined
          ? undefined
          : [
              {
                tank_id: product.tank_id,
                expected_gross_quantity: Number(product.gross_quantity),
              },
            ],
      expected_gross_quantity: Number(product.gross_quantity),
    }))

    // If now delivery exists, creat new and dont
    if (!existingDelivery) {
      console.time("Add Del: new del api")
      await axios.post("event/loads/", {
        type: "CREATE_DELIVERY",
        load_id,
        data: {
          customer_id: data.customer,
          location_id: data.location,
          assigned_salesperson: data.assigned_salesperson,
          po_number: data.po_number,
          notes: data.notes ? data.notes : undefined,
          note_image: data.note_image || "",
          accessorial_items: data.accessorials ?? [],
          products: newDeliveryProducts,
        },
      })
      await eventApi("MATCH_PICKUP_DELIVERY", load_id)
      console.timeEnd("Add Del: new del api")
      return true
    }
    console.time("Add Del: process")
    // If delivery exists, see if you can merge products

    newDeliveryProducts =
      existingDelivery.products.map((product: any) => ({
        id: product.id,
        product_id: product.product.id,
        tanks:
          product.tank_id === undefined
            ? undefined
            : [
                {
                  tank_id: product.tank_id,
                  expected_gross_quantity: Number(
                    product.expected_gross_quantity
                  ),
                },
              ],
        expected_gross_quantity: Number(product.expected_gross_quantity),
      })) ?? []
    data?.delivery_products.forEach((product: any) => {
      //If product already exists in delivery, add the quantity

      const existingIndex = existingDelivery.products.findIndex(
        (ed: any) => ed.product.id === product.product
      )

      if (true) {
        newDeliveryProducts.push({
          product_id: product.product,
          tanks:
            product.tank_id === undefined
              ? undefined
              : [
                  {
                    tank_id: product.tank_id,
                    expected_gross_quantity: Number(product.gross_quantity),
                  },
                ],
          expected_gross_quantity: Number(product.gross_quantity),
        })
        return
      }

      // If id is present, override the quantity, else add the quantity
      const oldQuantity = product.id
        ? 0
        : Number(newDeliveryProducts[existingIndex].expected_gross_quantity)

      newDeliveryProducts[existingIndex].expected_gross_quantity =
        oldQuantity + Number(product.gross_quantity)
    })
    // TODO deliveries mid way will be affected
    // await axios.post('event/loads/', {
    //   type: 'DELETE_DELIVERY',
    //   load_id,
    //   data: {
    //     delivery_id: existingDelivery.id
    //   }
    // })
    console.time("Add Del: update del api")
    await axios.post("event/loads/", {
      type: "UPDATE_DELIVERY",
      load_id,
      data: {
        delivery_id: existingDelivery.id,
        customer_id: data.customer,
        po_number: data.po_number,
        assigned_salesperson: data.assigned_salesperson,
        location_id: data.location,
        notes: data.notes,
        note_image: data.note_image,
        products: newDeliveryProducts,
      },
    })
    console.timeEnd("Add Del: update del api")
    console.timeEnd("Add Del: process")
    return true
  }

  return addDelivery
}

export const useHookToCleanLoads = (load_id: string | undefined) => {
  const { refetch } = useQuery(["load", load_id], fetchSingleLoad(load_id), {
    enabled: false,
  })
  const clean = async () => {
    // await axios.post(`event/loads/${load_id}/reorder/`)
    // await eventApi("MATCH_PICKUP_DELIVERY", load_id)
    // const { data } = await refetch()
    // await cleanLoads(data)
    // refetch()
  }

  return clean
}

const keyHandler = (handler: Function) => (e: any) => {
  if (!e.shiftKey || !e.ctrlKey) return
  handler(e)
}

export const useKey = (handler: Function, ignore?: boolean) => {
  useEffect(() => {
    return
    if (ignore) return
    document.addEventListener("keydown", keyHandler(handler) as any)
    return () =>
      document.removeEventListener("keydown", keyHandler(handler) as any)
  })
}
